import React, { memo, useRef, useEffect } from 'react';
import { useStoreApi } from '@xyflow/react';

interface InverseScaleBoxProps {
  children: React.ReactNode;
  className?: string;
  style?: React.CSSProperties;
  scaleOrigin?: string;
  /**
   * 内容和 padding 在 zoom < minZoom 时按比例缩小（跟随画布缩放）
   * 不设置时，始终反向缩放保持固定尺寸（原行为）
   */
  minZoom?: number;
  /**
   * 内层抵消机制 — 内容保持固定尺寸
   * - true: 内层额外缩放抵消外层缩放，保持内容固定尺寸
   *   - 无 minZoom: 全范围内容固定、padding 固定（用于底部参数面板）
   *   - 有 minZoom 且 zoom < minZoom: 内容固定、padding 按比例缩小（用于顶部栏 50%-10%）
   *   - 有 minZoom 且 zoom >= minZoom: 内容固定、padding 固定（用于顶部栏 50%-300%）
   * - false/undefined: 内容跟随画布缩放（用于标题）
   */
  fixedContent?: boolean;
  /**
   * 【已退役】小屏适配开关：画布工作区已流式化（与首页同方案），节点内面板
   * 统一原生尺寸显示、随画布 zoom 缩放，不再按视口宽/1920 缩放。prop 保留
   * 兼容既有调用点，内部恒忽略。
   */
  fitViewport?: boolean;
}

/**
 * 反向缩放容器 — 让子元素在画布缩放时保持视觉尺寸不变
 *
 * 原理：用 transform: scale(1/zoom) 抵消父级 viewport 的 scale(zoom)
 *
 * 订阅源：ReactFlow 的 store（transform[2] 是实时 zoom）
 * 之前订阅 useCanvasStore.zoom，但 useCanvasStore 通过 60ms debounce 更新
 * → 缩放画布时顶部栏/底部面板滞后 60ms 才更新反向缩放
 * 现在直接订阅 ReactFlow store，每帧实时响应画布缩放
 *
 * 性能：直接操作 DOM（el.style.transform），不触发 React 重渲染
 *
 * 流式自适应说明（对齐首页方案）：历史上本组件还叠加了「视口宽/1920」的
 * 小屏适配系数（fitViewport），随整页缩放体系于本轮一并退役——节点内面板
 * 视觉尺寸只由画布 zoom 决定，窗口/视口变化不再引入额外缩放因子。
 *
 * 三种模式：
 * 1. 无 minZoom + fixedContent（底部参数面板）：
 *    - 外层 scale(1/zoom)，内层 scale(1) → 内容固定、padding 固定（全范围）
 * 2. 有 minZoom + fixedContent（顶部栏）：
 *    - zoom >= minZoom: 外层 scale(1/zoom)，内层 scale(1) → 内容固定、padding 固定
 *    - zoom < minZoom: 外层 scale(1/minZoom)，内层 scale(minZoom/zoom)
 *      → 内容固定、padding 按比例缩小（2×zoom）
 * 3. 有 minZoom 无 fixedContent（标题）：
 *    - zoom >= minZoom: 外层 scale(1/zoom) → 内容固定、padding 固定
 *    - zoom < minZoom: 外层 scale(1/minZoom) → 内容按比例缩小、padding 按比例缩小
 */
export const InverseScaleBox: React.FC<InverseScaleBoxProps> = memo(({ children, className, style, scaleOrigin, minZoom, fixedContent }) => {
  const outerRef = useRef<HTMLDivElement>(null);
  const innerRef = useRef<HTMLDivElement | null>(null);
  const baseTransform = style?.transform || '';
  const store = useStoreApi();

  useEffect(() => {
    const outer = outerRef.current;
    if (!outer) return;

    const applyZoom = (zoom: number) => {
      // effectiveZoom: 当 zoom < minZoom 时，固定在 minZoom
      // 这样外层的 scale(1/effectiveZoom) 会让内容和 padding 按 zoom/minZoom 比例缩小
      const effectiveZoom = (minZoom && zoom < minZoom) ? minZoom : zoom;
      outer.style.transform = `${baseTransform} scale(${1 / effectiveZoom})`;
      // fixedContent=true: 内层额外缩放抵消外层缩放，保持内容固定尺寸
      // 内层 scale = effectiveZoom/zoom
      // 内容总缩放 = (1/effectiveZoom) * (effectiveZoom/zoom) * zoom(父级) = 1（固定）
      // - 无 minZoom: effectiveZoom=zoom，内层 scale(1)，padding 受外层 scale(1/zoom) 抵消父级 → padding 固定
      // - 有 minZoom 且 zoom<minZoom: 内层 scale(minZoom/zoom)，padding 只受外层影响 → 按比例缩小
      if (fixedContent && innerRef.current) {
        innerRef.current.style.transform = `scale(${effectiveZoom / zoom})`;
      }
    };

    // 初始值
    applyZoom(store.getState().transform[2]);

    // 订阅 ReactFlow store 的 transform 变化（实时，无 debounce）
    let prevZoom = store.getState().transform[2];
    const unsub = store.subscribe((state) => {
      const zoom = state.transform[2];
      if (zoom !== prevZoom) {
        prevZoom = zoom;
        applyZoom(zoom);
      }
    });

    return () => {
      unsub();
    };
  }, [baseTransform, store, minZoom, fixedContent]);

  const transformOrigin = scaleOrigin || style?.transformOrigin;

  return (
    // pointer-events 命中区修复：fixedContent 双层结构下，外层布局盒是全设计稿尺寸（如底部
    // 参数面板 660px 宽），内层内容缩放后命中盒（660）大于可见面板（660×内层缩放），四周留有
    // 一圈透明死区：点击死区落在节点 wrapper 内，React Flow 按节点交互处理、pane click 不触发
    // → 面板外近处点击无法取消节点选择（须点到更远处才生效）。故 fixedContent 时外层退化为
    // 纯视觉定位层（none），交互全部移入内层（auto，命中盒恰为可见面板；同时覆盖调用方
    // inline 的 pointerEvents:'auto'）；非 fixedContent（标题/多选工具栏）为单层结构，
    // transform 加在自身布局元素上、命中盒与视觉一致无死区，保持原行为
    <div
      ref={outerRef}
      className={className}
      style={{ ...style, transformOrigin, ...(fixedContent ? { pointerEvents: 'none' } : {}) }}
    >
      {fixedContent ? (
        <div ref={innerRef} style={{ transformOrigin, width: '100%', pointerEvents: 'auto' }}>
          {children}
        </div>
      ) : (
        children
      )}
    </div>
  );
});
